import { useEffect, useRef, type ElementType, type ReactNode } from "react";
import { prefersReducedMotion } from "@/utils/motion";

interface ScrollRevealProps {
  as?: ElementType;
  children: ReactNode;
  /** Stagger delay in milliseconds — use in multiples of ~90ms for a sequence. */
  delay?: number;
  className?: string;
}

/**
 * Fades/settles content into place the first time it scrolls into view.
 * Renders fully visible by default (correct for prerendered HTML and no-JS);
 * only opts into the displaced "about to reveal" state once mounted, motion
 * is welcome, and IntersectionObserver exists. See global.css `.reveal-pending`.
 */
export function ScrollReveal({
  as: Component = "div",
  children,
  delay = 0,
  className,
}: ScrollRevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || typeof IntersectionObserver === "undefined") return;

    el.style.setProperty("--reveal-delay", `${delay}ms`);
    el.classList.add("reveal-pending");

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add("reveal-visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [delay]);

  return (
    <Component ref={ref} className={className}>
      {children}
    </Component>
  );
}
